import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import api from '../services/api';

export interface Note {
  id: number;
  content: string;
  author_name: string;
  created_at: string;
}

export type EntityType = 'candidate' | 'job' | 'application';

interface UseNotesOptions {
  entityType: EntityType;
  entityId: number | string;
  enabled?: boolean;
}

interface UseNotesReturn {
  notes: Note[];
  isLoading: boolean;
  newNote: string;
  setNewNote: (value: string) => void;
  handleAddNote: () => void;
  isCreating: boolean;
}

/**
 * Custom hook for managing notes on any entity (candidate, job, application)
 *
 * @example
 * const { notes, isLoading, newNote, setNewNote, handleAddNote, isCreating } = useNotes({
 *   entityType: 'candidate',
 *   entityId: candidateId,
 * });
 */
export function useNotes({
  entityType,
  entityId,
  enabled = true,
}: UseNotesOptions): UseNotesReturn {
  const queryClient = useQueryClient();
  const [newNote, setNewNote] = useState('');

  const numericId = typeof entityId === 'string' ? Number.parseInt(entityId, 10) : entityId;
  const queryKey = [`${entityType}-notes`, entityId];

  // Fetch notes
  const { data: notesData, isLoading } = useQuery(
    queryKey,
    () => api.recruiting.getNotes(entityType, numericId),
    {
      enabled: enabled && !!entityId,
    }
  );

  // Create note mutation
  const createNoteMutation = useMutation(
    async (noteContent: string) => {
      return await api.recruiting.createNote({
        entity_type: entityType,
        entity_id: numericId,
        content: noteContent,
      });
    },
    {
      onSuccess: () => {
        queryClient.refetchQueries(queryKey);
        setNewNote('');
      },
      onError: (error: unknown) => {
        console.error('Error creating note:', error);
      },
    }
  );

  const handleAddNote = () => {
    if (newNote.trim()) {
      createNoteMutation.mutate(newNote.trim());
    }
  };

  return {
    notes: (notesData?.data || []) as Note[],
    isLoading,
    newNote,
    setNewNote,
    handleAddNote,
    isCreating: createNoteMutation.isLoading,
  };
}

export default useNotes;
